export const DEFAULT_ZOOM_IDX = 2;

export type AppState = {
  mode: "once" | "loop" | "through" | "delay";
  position: number;
  duration: number;
  markers: Marker[];
  editingMarkers: Marker[] | null;
  // currentSectionIdx: where the playhead is. not necessarily what's
  // selected. basically a computed property from position and markers.
  currentSectionIdx: number;
  // activeSectionIdx: The one that's highlighted.
  activeSectionIdx: number;
  activeMarkerIdx: number | null;
  scrubbing: number | false;
  muted: boolean;
  playing: boolean;
  following: boolean;
  // Zoom can deviate from zoomIdx while animating zoom. ZoomIdx is to be used
  // for displaying the current zoom and updating the zoom. Zoom is used for
  // rendering.
  zoomIdx: number;
  zoom: number;
  centerProgress: number | null;
  trackId: string;
  trackError: any;
  history: EditState[];
  historyIdx: number; // for undo/redo
};
export type EditState = Pick<AppState, "markers">;
export type TrackId = string;

export type SerializedState = Pick<
  AppState,
  "history" | "historyIdx" | "duration"
> & {
  modified: number;
};

export type TrackStorage = {
  [k in TrackId]: SerializedState;
};

export type Updates = Partial<AppState>;

export type ActiveSection = number;

export type Player = {
  seek: (timeMs: number) => Promise<void>;
  next: null | (() => Promise<void>);
  prev: null | (() => Promise<void>);
  pause: () => Promise<void>;
  togglePlay: () => Promise<void>;
  onStateChange: (fn: OnStateChange) => void;
  load: (track: TrackId) => void;
  validate: (input: string) => TrackId | null;
  mute: () => void;
  unmute: () => void;
};

export type Marker = { pos: number };
export type Breakpoint = number;
export type Interval = { start: number; end: number };
export type Section = Interval;

export type OnStateChange = (st: Updates) => void;

export function size(interval: Interval): number {
  return Math.abs(interval.end - interval.start);
}
export function surrounds(n: number, interval: Interval): boolean {
  return interval.start < n && n < interval.end;
}
export function includes(n: number, interval: Interval): boolean {
  return interval.start <= n && n <= interval.end;
}
export function clamp(n: number, interval: Interval): number {
  return Math.min(Math.max(interval.start, n), interval.end);
}
export function isApprox(n: number, goal: number, tolerance: number): boolean {
  return includes(n, { start: goal - tolerance, end: goal + tolerance });
}

export function wrap(n: number, interval: Interval): number {
  const len = interval.end - interval.start;
  let rem = (n - interval.start) % len;
  if (rem < 0) {
    rem = len + rem;
  }
  return rem + interval.start;
}

export function sortMarkers(m1: Marker, m2: Marker): number {
  return m1.pos - m2.pos;
}

export function getBreakpoints(
  markers: Marker[],
  duration: number,
): Breakpoint[] {
  // assumes markers aren't past the duration, otherwise things might get
  // weird.
  return [0, ...markers.map((m) => m.pos), duration];
}

export function throttle<F extends (...args: any) => any>(fn: F, ms: number) {
  let lastTime = 0;
  return (...args: Parameters<F>) => {
    const d = Date.now();
    if (d - lastTime > ms) {
      lastTime = d;
      fn.apply(null, args);
    }
  };
}

export const ZOOMS = [
  1, 1.5, 2, 3, 4, 6, 8, 12, 16, 24, 32, 48, 64, 128, 256, 512, 1024,
];

export const ZOOM_MAX = ZOOMS.length - 1;

export function zoomToIdx(zoom: number) {
  return ZOOMS.findIndex((z) => zoom <= z);
}

export function idxToZoom(idx: number) {
  return ZOOMS[idx];
}
